import useAnalysis from "./useAnalysis"
import { useLocation } from "react-router-dom"
import SectionSelector from "./components/SectionSelector/SectionSelector"
import LoadingView from "../../components/LoadingView/LoadingView"
import ControlSelector from "./components/ControlSelector/ControlSelector"
import AnalysisControls from "./components/AnalysisControls/AnalysisControls"
import useAnalysisControls from "./components/AnalysisControls/useAnalysisControls"
import ErrorBanner from "../../components/ErrorBanner/ErrorBanner"
import THzSection from "./components/THzSection/THzSection"
import WorkflowInfoSection from "./components/WorflowInfoSection/WorkflowInfoSection"
import ScanMediaSection from "./components/ScanMediaSection/ScanMediaSection"
import MetalSection from "./components/MetalSection/MetalSection"
import ThermalSection from "./components/ThermalSection/ThermalSection"
import MotionSection from "./components/MotionSection/MotionSection"
import useExportWorkflowData from "../../hooks/useExportWorkflowData"
import useReportControls from "./components/ReportControls/useReportControls"
import ReportControls from "./components/ReportControls/ReportControls"
import BackIcon from "../../media/BackIcon"
import Button from "../../components/Button/Button"
import "./Analysis.css"
import useLoadWorkflow from "../../hooks/useLoadWorkflow"
import { ToastContainer } from "react-toastify"

const AnalysisDashboard = () => {

  const location = useLocation()

  const [{
    sections,
    sectionTHzHeatmaps,
    thzMetadata,
    imuData,
    thermalData,
    motionData,
    scanMedia,
    generalError
  }] = useLoadWorkflow()

  const [{
    graphRef,
    displayWidth,
    selectedSection,
    selectedScan,
    selectedControl,
    onSelectSection,
    onSelectScan,
    onSelectControl,
    onDownloadReport,
    isDownloadingReport,
    reportError,
    onHideReportError,
    onBack
  }] = useAnalysis({thzMetadata: thzMetadata.metadata})

  const [{
    overlayAnalysisCheckbox,
    minHighlightCheckbox,
    maxHighlightCheckbox
  }] = useAnalysisControls()

  const [{
    showTHzHeatmapCheckbox,
    showScanImagesCheckbox,
    showTHzGraphsCheckbox,
  }] = useReportControls()

  const [{
    ellipsisMenuWorkflow,
    ellipsisMenuThz,
    ellipsisMenuMetal,
    ellipsisMenuMotion,
    ellipsisMenuThermal,
    ellipsisMenuMedia
  }] = useExportWorkflowData()
  
  const analysisControlComponents = [
    <AnalysisControls 
      overlayCheckbox={overlayAnalysisCheckbox}
      showMinCheckbox={minHighlightCheckbox}
      showMaxCheckbox={maxHighlightCheckbox}
      version={sectionTHzHeatmaps?.heatmaps?.[selectedSection].version ?? "Unkown"}
    />,

    <ReportControls
      showTHzHeatmapCheckbox={showTHzHeatmapCheckbox}
      showTHzGraphsCheckbox={showTHzGraphsCheckbox}
      showScanImagesCheckbox={showScanImagesCheckbox}
      isDownloadingReport={isDownloadingReport}
      onGenerateReport={onDownloadReport}
    />
  ]

  if (generalError !== null) {
    return (
      <>
        <ErrorBanner show={true} error={generalError ?? new Error("Failed to fetch.")} />
        <LoadingView text="" isLoading={true} showWheel={false}>
          <div className="error-container">
            <h3>Failed to load workflow. Please try again.</h3>
            <Button title="Back" onClick={onBack} type="full" theme="light" />
          </div>
        </LoadingView>
      </>
    )
  }

  return (
    <div className="analysis-container">
      <ErrorBanner
        show={reportError !== undefined}
        error={reportError ?? new Error("Failed to fetch.")}
        onClose={onHideReportError}
      />
      <div className="analysis-header">
        <div className="analysis-back-button" onClick={onBack}>
          <BackIcon />
          <p>Back</p>
        </div>
        <h2 className="analysis-topbar-title">{location.state.pipe_id ?? "none"}</h2>
      </div>

      <SectionSelector
        pipeId={location.state.workflow?.pipe_id ?? "none"}
        sections={sectionTHzHeatmaps.heatmaps?.map((_, i) => i) ?? []} 
        selectedSection={selectedSection} 
        onSelect={onSelectSection}
        isLoading={(sectionTHzHeatmaps.heatmaps?.length ?? 0) < (sections?.sections?.length ?? 1)}
      />

      <div className="analysis-control-container">
        <div className="analysis-display">

          <WorkflowInfoSection
            workflow={location.state}
            section={sections?.sections?.[selectedSection]}
            ellipsisMenu={ellipsisMenuWorkflow}             
          />

          <THzSection
            colourSamples={sectionTHzHeatmaps?.heatmaps?.[selectedSection].thz_heatmap_image}
            isolatedScans={thzMetadata?.metadata?.[selectedSection]}
            selectedScan={selectedScan}
            onSelect={onSelectScan}
            width={displayWidth}
            vectorOverlays={[]}
            pixelHeight={8}
            ellipsisMenu={ellipsisMenuThz}
            showOverlay={false}
            minDataPoint={{ value:  0, show: minHighlightCheckbox.value }}
            maxDataPoint={{ value: 255, show: maxHighlightCheckbox.value }}
          />

          <MetalSection
            heatmapData={imuData.samples?.[selectedSection]?.[selectedScan]}
            isolatedScan={thzMetadata.metadata?.[selectedSection]?.[selectedScan]}
            width={displayWidth}
            pixelHeight={50}
            ellipsisMenu={ellipsisMenuMetal}      
          />

          <ThermalSection
            heatmapData={thermalData?.samples?.[selectedSection]?.[selectedScan]}
            sectionThermalData={thermalData?.samples?.[selectedSection]}
            analysisHeatmap={[]}
            isolatedScan={thzMetadata.metadata?.[selectedSection]?.[selectedScan]}
            width={displayWidth}
            pixelHeight={16}
            ellipsisMenu={ellipsisMenuThermal}
          />

          <MotionSection
            motion={motionData?.samples?.[selectedSection]?.[selectedScan]}
            width={displayWidth}
            ellipsisMenu={ellipsisMenuMotion}
          />

          <ScanMediaSection 
            media={scanMedia?.images?.[selectedSection]}
            ellipsisMenu={ellipsisMenuMedia}          
          />

          <div className="analysis-section" style={{opacity: 0}}>
            <p ref={graphRef}>Subtera</p>
          </div>
        </div>
        
        <div className="controls-container">
          <ControlSelector
            options={["Analysis", "Report"]}
            selected={selectedControl}
            onSelect={(i) => onSelectControl(i)}
          />

        <div className="analysis-controls">
          {analysisControlComponents[selectedControl]}
        </div>
        </div>
      </div>
      <ToastContainer />
      {/* <ExpiredTokenAlert show={showSignedOut} onConfirm={onConfirmSignedOut} /> */}
    </div>
  )
}

export default AnalysisDashboard
